/**
 * PlayScreen - the field, Pip, and the prompt.
 *
 * Pure presentation: it holds no game state and makes no decisions. Everything
 * it draws arrives as props from the trial loop.
 *
 * Layout is three stacked bands - progress, field, prompt - which is the shape
 * that survives a phone in portrait, a phone in landscape and a tablet without
 * three separate designs.
 */

import { useRef } from 'react';
import PipCharacter from '../../characters/PipCharacter';
import Bubble from '../parts/Bubble';
import PromptCard from '../parts/PromptCard';

/** The grown-ups button opens on a press and hold, so a stray tap by the child
 *  cannot interrupt a trial. 700ms is long enough to be deliberate and short
 *  enough that an adult does not think it is broken. */
const HOLD_MS = 700;

export default function PlayScreen({ view, pip, banner, listening, interim, onOpenPanel }) {
  // Sorted by slot, because the slot is the whole point: shared/rounds.js
  // shuffles positions independently of the draw so that a child cannot learn
  // "it is always the left one" instead of learning the word.
  const live = view.bubbles
    .filter((b) => b.state !== 'gone')
    .sort((a, b) => a.slot - b.slot);
  const holdRef = useRef(null);

  // Pip's speech bubble carries whatever the prompt card and the banner are
  // not already showing - the clue, the assist, the level intro. Three copies
  // of one sentence is noise, and this screen is for a child.
  const pipBubble = pip.bubble && pip.bubble !== view.prompt && pip.bubble !== (banner && banner.text)
    ? pip.bubble
    : '';

  const startHold = () => {
    clearTimeout(holdRef.current);
    holdRef.current = setTimeout(onOpenPanel, HOLD_MS);
  };
  const cancelHold = () => clearTimeout(holdRef.current);

  return (
    <div className="bp-play">
      <header className="bp-play__top">
        <div className="bp-progress" aria-label={`Round ${view.round + 1} of ${view.rounds}`}>
          {Array.from({ length: view.rounds }).map((_, i) => (
            <span key={i} className={`bp-progress__dot${i < view.round ? ' is-done' : ''}${i === view.round ? ' is-now' : ''}`} />
          ))}
        </div>

        {/* Deliberately small, unlabelled and out of the child's way. */}
        <button
          type="button"
          className="bp-play__grownups"
          onPointerDown={startHold}
          onPointerUp={cancelHold}
          onPointerLeave={cancelHold}
          onPointerCancel={cancelHold}
          onContextMenu={(e) => e.preventDefault()}
          aria-label="Grown-ups: press and hold"
          title="Press and hold"
        >
          <span aria-hidden="true">•••</span>
        </button>
      </header>

      <div className={`bp-field bp-field--count-${live.length}`}>
        {live.map((bubble) => (
          <Bubble
            key={`${bubble.objectId}-${bubble.slot}`}
            bubble={bubble}
            highlighted={view.highlight === bubble.objectId}
            pointing={view.pointing === bubble.objectId}
            listening={listening.isVocalising}
          />
        ))}

        {banner && (
          <div className={`bp-banner bp-banner--${banner.kind}`} role="status">
            {banner.text}
          </div>
        )}
      </div>

      <div className="bp-play__pip">
        <PipCharacter
          mood={pip.mood}
          animation={pip.animation}
          bubble={pipBubble}
          size="medium"
        />
        <div className="bp-basket" aria-hidden="true">🧺</div>
      </div>

      <PromptCard
        prompt={view.prompt}
        cue={view.cue}
        chips={view.chips}
        hint={view.hint}
        listening={listening}
        interim={interim}
        rungId={view.rungId}
      />
    </div>
  );
}
